import calendar from '@/assets/calendar.svg'
import { DateValue, parseDate, parseDateTime } from '@internationalized/date'
import { DatePicker } from '@nextui-org/date-picker'
import { I18nProvider } from '@react-aria/i18n'
import React, { FC, useEffect, useRef, useState } from 'react'
import { FormState } from '../survey/SurveyModel/types/commonTypes'
import Errors from './Error'

interface ICustomizedDatePicker {
    onChange: (formattedTime: string | null) => void
    onBlur?: () => void
    initialDate?: string | null
    isPatient?: boolean
    form?: FormState
    isInValid?: boolean
    showErrors?: boolean
    name?: string
    disabled?: boolean
    showTime?: boolean
    className?: string
}

const CustomizedDatePicker: FC<ICustomizedDatePicker> = (
    props: ICustomizedDatePicker
) => {
    const {
        onChange,
        disabled,
        isPatient = false,
        onBlur,
        initialDate,
        form,
        showErrors,
        name,
        isInValid,
        showTime,
        className = ''
    } = props
    const [date, setDate] = React.useState<DateValue | null>()
    const isInitialRun = useRef(true)
    const [isFocused, setIsFocused] = useState(false)
    const [isActive, setIsActive] = useState(false)
    const [isOpen, setIsOpen] = useState(false)

    const convertStringToDateValue = (dateStr: string): DateValue => {
        return showTime ? parseDateTime(dateStr.replace('Z', '')) : parseDate(dateStr) // Parses "YYYY-MM-DD" into a DateValue object
    }

    useEffect(() => {
        if (isInitialRun.current && initialDate) {
            isInitialRun.current = false
            const date = initialDate
            setDate(convertStringToDateValue(date))
        }
    }, [initialDate])

    const formatUKDate = (date: DateValue) => {
        const day = String(date.day).padStart(2, '0')
        const month = String(date.month).padStart(2, '0')
        const year = date.year
        return `${year}-${month}-${day}`
    }

    const handleDateChange = (newDate: DateValue | null) => {
        setDate(newDate)
        isInitialRun.current = false
        if (newDate) {
            if (showTime) {
                return onChange(newDate.toString());
            }
            const formattedDate = newDate
                ? formatUKDate(newDate as DateValue)
                : null
            onChange(formattedDate)
        } else {
            onChange(null)
        }
    }

    const handleOnBlure = () => {
        if (onBlur) onBlur()
        setIsFocused(false)
        setIsActive(false)
    }

    const clearDateRange = () => {
        setDate(null)
        onChange(null)
    }
    const closeCalendar = () => setIsOpen(false)

    return (
        <div className={`date-range-picker ${isInValid ? 'error' : ''} ${className}`}>
            <div>
                <div
                    className={`input-div ${isOpen ? 'input-div-open' : ''} ${isFocused ? 'input-div-focus' : ''} ${isActive ? 'input-div-active' : ''}`}
                >
                    <I18nProvider locale="en-GB">
                        <DatePicker
                            disableAnimation
                            className={`custom-font-family customize-daterange max-h-[36px] ${isPatient ? '' : ' customize-daterange-poa'} ${disabled ? 'disabled' : ''}`}
                            classNames={{
                                popoverContent:
                                    '-translate-y-[5px] border-0 ring-0 outline-none !shadow-[0_4px_8px_0_rgba(63,63,63,0.14),4px_6px_10px_-6px_rgba(63,63,63,0.12),-4px_6px_10px_-6px_rgba(63,63,63,0.12)]',
                            }}
                            popoverProps={{
                                className:
                                    'before:shadow-none shadow-none ring-0 border-0',
                            }}
                            onChange={handleDateChange}
                            isOpen={isOpen}
                            granularity={showTime ? 'minute' : 'day'}
                            onBlur={handleOnBlure}
                            minValue={parseDate('1900-04-08')}
                            value={date}
                            timeInputProps={showTime ?{
                              label: 'Start time (24hr)',
                              className: "poa-theme custom-time-input",
                              
                            } : undefined}
                            calendarWidth={300}
                            onFocus={() => setIsFocused(true)}
                            calendarProps={{
                                classNames: {
                                    base: 'bg-background custom-font-family shadow-none border-0 ring-0',
                                    headerWrapper:
                                        'pt-4 bg-background custom-font-family',
                                    prevButton:
                                        'border-1 border-default-200 rounded-small custom-header-color  font-custom',
                                    nextButton:
                                        'border-1 border-default-200 rounded-small custom-header-color font-custom',
                                    gridHeader:
                                        'bg-background !shadow-none border-b-0 custom-header-color font-custom custom-font-family',
                                    cellButton: [
                                        'data-[today=true]:bg-default-100 data-[selected=true]:bg-transparent rounded-small',
                                        'data-[range-start=true]:before:rounded-l-small',
                                        'data-[selection-start=true]:before:rounded-l-small',
                                        'data-[range-end=true]:before:rounded-r-small',
                                        'data-[selection-end=true]:before:rounded-r-small',
                                        'data-[selected=true]:data-[selection-start=true]:data-[range-selection=true]:rounded-small',
                                        'data-[selected=true]:data-[selection-end=true]:data-[range-selection=true]:rounded-small',
                                    ],
                                },
                            }}
                            CalendarBottomContent={
                                <div>
                                    <div className="flex justify-between calendar-bottom">
                                        <button
                                            className="clear"
                                            onClick={() => clearDateRange()}
                                        >
                                            Clear
                                        </button>
                                        <button
                                            className="OK"
                                            onClick={() => closeCalendar()}
                                        >
                                            OK
                                        </button>
                                    </div>
                                </div>
                            }
                            selectorButtonProps={{
                                onClick: () => {
                                    setIsOpen((prev) => !prev);
                                }
                            }}
                            selectorIcon={
                                <button className="custom-icon">
                                    <img
                                        src={calendar}
                                        alt="Calendar"
                                        className="w-4 h-4"
                                    />
                                </button>
                            }
                        />
                    </I18nProvider>
                </div>
            </div>
            {form && showErrors && name ? (
                <Errors
                    fieldName={name}
                    validation={form[name] ? form[name] : undefined}
                />
            ) : (
                ''
            )}
        </div>
    )
}

export default CustomizedDatePicker
